import { Heading1 } from 'components/shared/Typography';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  spacing
} from 'theme';
import { getPublicPath } from 'utils/paths';

export const StyledWrapper = styled.div`
  background-color: ${color('navy_100')};
`;

export const StyledContainer = styled.div`
  max-width: ${container('xl')};
  padding: ${spacing('xl5')} ${spacing('md')};
  margin: auto;
`;

export const StyledBillboard = styled.div`
  background-image: ${() =>
    `url('${getPublicPath(
      '/images/stateOfHiring/ellipses.svg'
    )}'), linear-gradient(116.1deg, #002240 0.64%, #147092 100%)`};
  background-repeat: no-repeat;
  background-position: top right 10%, top right;
  background-size: auto 120%, cover;
  color: ${color('white')};
  font-size: ${fontSize('lg')};
  line-height: 1.6;
  text-align: center;

  @media (min-width: ${screen('md')}) {
    text-align: left;
    font-size: ${fontSize('xl2')};
    line-height: 1.58;
  }

  p {
    margin-bottom: 0;
  }

  ${StyledContainer} {
    padding-top: ${spacing('xl4')};
    padding-bottom: ${spacing('xl4')};

    @media (min-width: ${screen('md')}) {
      padding-top: ${spacing('xl6')};
      padding-bottom: ${spacing('xl6')};
    }
  }
`;

export const StyledBillboardContent = styled.div`
  max-width: 42rem;
`;

export const StyledBillboardTitle = styled(Heading1)`
  font-size: ${fontSize('xl5')};

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('xl6')};
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('xl7')};
  }
`;

export const StyledHeader = styled.div`
  text-align: center;
  max-width: ${container('md')};
  margin: auto;
  line-height: 1.5;

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('lg')};
    line-height: 1.6;
  }
`;

export const StyledContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl3')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: 2fr 1fr;
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: ${spacing('xl')};
  }
`;

export const StyledCard = styled.div`
  background-color: ${color('white')};
  padding: ${spacing('xl2')};
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${spacing('xl2')};
  border-radius: ${radius('lg')};
`;

export const StyledCardTitle = styled.h4`
  margin: 0;
  font-size: ${fontSize('md')};
  line-height: 1.25;
  text-transform: uppercase;
  font-weight: ${fontWeight('extraBold')};
  color: ${color('navy_500')};
`;

export const StyledData = styled.div`
  display: flex;
  flex-direction: column;

  gap: ${spacing('xl')};
  justify-content: space-between;

  @media (min-width: ${screen('md')}) {
    flex-direction: row;
  }

  &.vertical {
    flex-direction: column;
  }
`;

export const StyledDataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing('lg')};
  font-weight: ${fontWeight('extraBold')};
`;

export const StyledValue = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  color: ${color('navy_500')};
  font-size: ${fontSize('lg')};

  &::after {
    content: '';
    width: ${spacing('xl4')};
    height: ${spacing('xs')};
    background-color: ${color('navy_200')};
    position: absolute;
    bottom: -${spacing('sm')};
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const StyledValueNumber = styled.span`
  font-family: ${fontFamily('display')};
  font-size: ${fontSize('xl6')};
  line-height: 1;
  font-weight: ${fontWeight('semiBold')};
`;

export const StyledSource = styled.span`
  font-size: ${fontSize('xs')};
  line-height: 1.333;
  color: ${color('text_subtle')};

  strong {
    font-weight: ${fontWeight('extraBold')};
  }
`;

export const StyledStagesContent = styled.div`
  max-width: ${container('lg')};
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl3')};
  line-height: 1.5;

  @media (min-width: ${screen('md')}) {
    grid-template-columns: 2fr 1fr;
    font-size: ${fontSize('lg')};
    line-height: 1.6;
  }

  p {
    margin-top: 0;
  }

  ${StyledCardTitle} {
    color: ${color('text_subtle')};
    margin-bottom: ${spacing('xl')};
  }
`;

export const StyledHighlight = styled.div`
  background-color: ${color('navy_150')};
  color: ${color('navy_500')};
  padding: ${spacing('md')};
  border-radius: ${radius('md')};
  display: flex;
  align-items: center;
  gap: ${spacing('md')};
  font-weight: ${fontWeight('medium')};
  line-height: 1.5;
  font-size: ${fontSize('md')};
`;

export const StyledHighlightValue = styled.div`
  font-family: ${fontFamily('display')};
  font-size: ${fontSize('xl5')};
  line-height: 1;
  font-weight: ${fontWeight('semiBold')};
  text-transform: uppercase;
`;
